import { useState } from "react";
import { Target, Copy, AlertCircle, CheckCircle, HelpCircle } from "lucide-react";
import styles from "./ImageSelector.module.css";

export default function ImageSelector() {
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageName, setImageName] = useState('');
    const [cssSelector, setCssSelector] = useState('');
    const [pageUrl, setPageUrl] = useState('https://example.com');
    const [errors, setErrors] = useState({});
    const [showHelperModal, setShowHelperModal] = useState(false);
    const [testId] = useState(() => `test_${Date.now()}`);

    const validateSelector = (selector) => {
        if (!selector.trim()) {
            return 'CSS selector is required';
        }

        try {
            document.querySelector(selector);
            return null;
        } catch (error) {
            return 'Invalid CSS selector format';
        }
    };

    const validatePageUrl = (url) => {
        if (!url.trim()) {
            return 'Page URL is required';
        }

        try {
            new URL(url);
            return null;
        } catch (error) {
            return 'Invalid URL format';
        }
    };

    const handleNameChange = (value) => {
        setImageName(value);
        if (errors.name && value.trim()) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.name;
                return newErrors;
            });
        }
    };

    const handleSelectorChange = (value) => {
        setCssSelector(value);

        if (value.trim()) {
            const error = validateSelector(value);
            setErrors(prev => ({
                ...prev,
                selector: error
            }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.selector;
                return newErrors;
            });
        }
    };

    const handlePageUrlChange = (value) => {
        setPageUrl(value);

        if (value.trim()) {
            const error = validatePageUrl(value);
            setErrors(prev => ({
                ...prev,
                pageUrl: error
            }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.pageUrl;
                return newErrors;
            });
        }
    };

    const hasValidInput = () => {
        const nameValid = imageName.trim().length > 0;
        const selectorValid = cssSelector.trim().length > 0 && !errors.selector;
        const urlValid = pageUrl.trim().length > 0 && !errors.pageUrl;
        return nameValid && selectorValid && urlValid;
    };

    const handleSubmit = () => {
        const newErrors = {};

        if (!imageName.trim()) {
            newErrors.name = 'Image name is required';
        }

        const selectorError = validateSelector(cssSelector);
        if (selectorError) {
            newErrors.selector = selectorError;
        }

        const urlError = validatePageUrl(pageUrl);
        if (urlError) {
            newErrors.pageUrl = urlError;
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            const imageConfig = {
                name: imageName.trim(),
                selector: cssSelector.trim(),
                pageUrl: pageUrl.trim(),
                type: 'image',
                elementType: 'image',
                detectionMethod: 'manual'
            };

            setSelectedImage(imageConfig);
            showToast('✅ Image configured successfully!');
        }
    };

    const openSelectorTool = () => {
        setShowHelperModal(true);

        const targetUrl = pageUrl.trim() || 'https://example.com';

        const previewParams = new URLSearchParams({
            cf_element_selection: 'true',
            test_id: testId,
            mode: 'image_selector',
            return_url: window.location.href
        });

        const url = new URL(targetUrl);
        previewParams.forEach((value, key) => {
            url.searchParams.set(key, value);
        });

        const previewUrl = url.toString();

        window.open(previewUrl, '_blank', 'noopener,noreferrer');

        startListeningForSelection();
    };

    const startListeningForSelection = () => {
        const handleSelection = (event) => {
            if (event.data?.type === 'CF_ELEMENT_SELECTED' && event.data?.mode === 'image_selector') {
                const { selector, elementText, imageInfo } = event.data;

                setImageName(elementText || imageInfo?.alt || 'Selected Image');
                setCssSelector(selector);
                setShowHelperModal(false);
                setErrors({});

                window.removeEventListener('message', handleSelection);
            }
        };

        window.addEventListener('message', handleSelection);

        setTimeout(() => {
            window.removeEventListener('message', handleSelection);
        }, 600000);
    };

    const copyQueryParams = () => {
        const params = `?cf_element_selection=true&test_id=${testId}&mode=image_selector`;
        navigator.clipboard.writeText(params);
        showToast('✅ Query parameters copied to clipboard!');
    };

    const showToast = (message) => {
        const toast = document.createElement('div');
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #10b981;
            color: white;
            padding: 12px 16px;
            border-radius: 6px;
            font-size: 14px;
            z-index: 10000;
        `;

        document.body.appendChild(toast);

        setTimeout(() => {
            document.body.removeChild(toast);
        }, 3000);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h4 className={styles.title}>Image Configuration</h4>
                <p className={styles.description}>
                    Configure the image you want to test by providing a name and CSS selector.
                </p>
            </div>

            <div className={styles.form}>
                {/* Image Name Field */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Image Name <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="text"
                        value={imageName}
                        onChange={(e) => handleNameChange(e.target.value)}
                        placeholder="e.g., Hero Banner Image"
                        className={`${styles.input} ${errors.name ? styles.error : ''}`}
                    />
                    {errors.name && (
                        <div className={styles.errorMessage}>
                            <AlertCircle size={14} />
                            <span>{errors.name}</span>
                        </div>
                    )}
                    <div className={styles.inputHelp}>
                        Give your image a descriptive name for easy identification
                    </div>
                </div>

                {/* Page URL Field */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Page URL <span className={styles.required}>*</span>
                    </label>
                    <input
                        type="url"
                        value={pageUrl}
                        onChange={(e) => handlePageUrlChange(e.target.value)}
                        placeholder="e.g., https://yourstore.com/products/example-product"
                        className={`${styles.input} ${errors.pageUrl ? styles.error : ''}`}
                    />
                    {errors.pageUrl && (
                        <div className={styles.errorMessage}>
                            <AlertCircle size={14} />
                            <span>{errors.pageUrl}</span>
                        </div>
                    )}
                    <div className={styles.inputHelp}>
                        URL of the page where your image is located (used for previews and selection)
                    </div>
                </div>

                {/* CSS Selector Field */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        CSS Selector <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.selectorInputWrapper}>
                        <input
                            type="text"
                            value={cssSelector}
                            onChange={(e) => handleSelectorChange(e.target.value)}
                            placeholder="e.g., .hero-banner img or img[alt='product photo']"
                            className={`${styles.input} ${styles.selectorInput} ${errors.selector ? styles.error : ''}`}
                        />
                        <button
                            type="button"
                            onClick={openSelectorTool}
                            className={styles.helperButton}
                            title="Open image selector finder tool"
                        >
                            <Target size={16} />
                            Find Image
                        </button>
                    </div>
                    {errors.selector && (
                        <div className={styles.errorMessage}>
                            <AlertCircle size={14} />
                            <span>{errors.selector}</span>
                        </div>
                    )}
                    <div className={styles.inputHelp}>
                        CSS selector that targets your image element
                        <button
                            type="button"
                            className={styles.helpLink}
                            onClick={() => setShowHelperModal(true)}
                        >
                            <HelpCircle size={12} />
                            Need help?
                        </button>
                    </div>
                </div>

                {/* Submit Button */}
                <div className={styles.actions}>
                    <button
                        onClick={handleSubmit}
                        disabled={!hasValidInput()}
                        className={styles.submitButton}
                    >
                        <CheckCircle size={16} />
                        Use This Image
                    </button>
                </div>
            </div>

            {/* Helper Modal */}
            {showHelperModal && (
                <div className={styles.modal}>
                    <div className={styles.modalOverlay} onClick={() => setShowHelperModal(false)} />
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3>🖼️ Image Selector Finder Tool</h3>
                            <button
                                onClick={() => setShowHelperModal(false)}
                                className={styles.closeButton}
                            >
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepContent}>
                                    <h4>Preview window opened</h4>
                                    <p>A new tab opened with your store. If it didn't open, check your popup blocker.</p>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepContent}>
                                    <h4>Visit the right page</h4>
                                    <p>Navigate to the page where your image is located. Add these query parameters to any URL:</p>

                                    <div className={styles.copyBox}>
                                        <code>?cf_element_selection=true&test_id={testId}&mode=image_selector</code>
                                        <button onClick={copyQueryParams} className={styles.copyButton}>
                                            <Copy size={14} />
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.step}>
                                <div className={styles.stepNumber}>3</div>
                                <div className={styles.stepContent}>
                                    <h4>Click your image</h4>
                                    <p>Click on the image you want to test. The selector will be automatically copied and filled in this form.</p>
                                </div>
                            </div>
                        </div>

                        <div className={styles.modalActions}>
                            <button
                                onClick={() => setShowHelperModal(false)}
                                className={styles.gotItButton}
                            >
                                Got it!
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success State */}
            {selectedImage && (
                <div className={styles.successCard}>
                    <CheckCircle size={20} className={styles.successIcon} />
                    <div className={styles.successContent}>
                        <h5>Image Configured: {selectedImage.name}</h5>
                        <p>Selector: <code>{selectedImage.selector}</code></p>
                    </div>
                </div>
            )}
        </div>
    );
}